import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
// Note: react-beautiful-dnd is deprecated but still works with legacy-peer-deps
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import courseService from '../services/courseService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/LessonManagement.css';

const LessonManagement = () => {
  const { appId, courseId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reordering, setReordering] = useState(false);
  
  // Check if user has app_editor role
  const isAppEditor = currentUser?.roles?.some(role => 
    role.name === 'app_editor' || role.name === 'admin' || role.name === 'superadmin'
  );
  
  useEffect(() => {
    console.log('🔍 LessonManagement: courseId from params:', courseId, 'appId:', appId);
    if (courseId) {
      fetchCourseAndLessons();
    } else {
      console.error('🔍 LessonManagement: courseId is undefined, cannot fetch lessons');
      setError('Course ID is missing or invalid. Please try again or contact support.');
      setLoading(false);
    }
  }, [courseId, appId]);
  
  const fetchCourseAndLessons = async () => {
    try {
      setLoading(true);
      
      console.log('🔍 LessonManagement: Fetching data for courseId:', courseId, 'appId:', appId);
      
      // Fetch course details
      const courseData = await courseService.getCourse(appId, courseId);
      setCourse(courseData);
      
      // Fetch lessons for the course - no longer need to pass appId
      const lessonsData = await courseService.getLessons(courseId);
      setLessons(lessonsData);
      
      setError(null);
    } catch (err) {
      console.error('Error fetching course and lessons:', err);
      setError('Failed to load course and lessons. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteLesson = async (lessonId) => {
    if (window.confirm('Are you sure you want to delete this lesson? This action cannot be undone.')) {
      try {
        // No longer need to pass appId or courseId
        await courseService.deleteLesson(lessonId);
        // Remove the deleted lesson from the state
        setLessons(lessons.filter(lesson => lesson.id !== lessonId));
      } catch (err) {
        console.error('Error deleting lesson:', err);
        setError('Failed to delete lesson. Please try again later.');
      }
    }
  };
  
  const handleDragEnd = async (result) => {
    // Dropped outside the list
    if (!result.destination) {
      return;
    }
    
    // Reorder the lessons in the state
    const reorderedLessons = Array.from(lessons);
    const [removed] = reorderedLessons.splice(result.source.index, 1);
    reorderedLessons.splice(result.destination.index, 0, removed);
    
    // Update the state immediately for a responsive UI
    setLessons(reorderedLessons);
    
    // Save the new order to the server
    try {
      setReordering(true);
      const lessonIds = reorderedLessons.map(lesson => lesson.id);
      // Only passing courseId now
      await courseService.reorderLessons(courseId, lessonIds);
    } catch (err) {
      console.error('Error reordering lessons:', err);
      setError('Failed to save the new lesson order. Please try again later.');
      // Revert to the original order if there's an error
      fetchCourseAndLessons();
    } finally {
      setReordering(false);
    }
  };
  
  // Fallback if DnD doesn't work - move lesson up
  const handleMoveUp = async (index) => {
    if (index === 0) return; // Already at the top
    
    const reorderedLessons = Array.from(lessons);
    const temp = reorderedLessons[index];
    reorderedLessons[index] = reorderedLessons[index - 1];
    reorderedLessons[index - 1] = temp;
    
    setLessons(reorderedLessons);
    
    try {
      setReordering(true);
      const lessonIds = reorderedLessons.map(lesson => lesson.id);
      // Only passing courseId now
      await courseService.reorderLessons(courseId, lessonIds);
    } catch (err) {
      console.error('Error reordering lessons:', err);
      setError('Failed to save the new lesson order. Please try again later.');
      fetchCourseAndLessons();
    } finally {
      setReordering(false);
    }
  };
  
  // Fallback if DnD doesn't work - move lesson down
  const handleMoveDown = async (index) => {
    if (index === lessons.length - 1) return; // Already at the bottom
    
    const reorderedLessons = Array.from(lessons);
    const temp = reorderedLessons[index];
    reorderedLessons[index] = reorderedLessons[index + 1];
    reorderedLessons[index + 1] = temp;
    
    setLessons(reorderedLessons);
    
    try {
      setReordering(true);
      const lessonIds = reorderedLessons.map(lesson => lesson.id);
      // Only passing courseId now
      await courseService.reorderLessons(courseId, lessonIds);
    } catch (err) {
      console.error('Error reordering lessons:', err);
      setError('Failed to save the new lesson order. Please try again later.');
      fetchCourseAndLessons();
    } finally {
      setReordering(false);
    }
  };
  
  if (!isAppEditor) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          You do not have permission to access this page. Only app editors can manage lessons.
        </div>
      </div>
    );
  }
  
  return (
    <div className="lesson-management container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Lesson Management</h1>
          {course && (
            <h5 className="text-muted">Course: {course.title}</h5>
          )}
        </div>
        <div className="d-flex gap-2">
          <Link to={`/apps/${appId}/courses/${courseId}/lessons/new`} className="btn btn-primary">
            <i className="fas fa-plus"></i> Add New Lesson
          </Link>
          <Link to={`/courses/${courseId}`} className="btn btn-secondary">
            <i className="fas fa-arrow-left"></i> Back to Course
          </Link>
        </div>
      </div>
      
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      
      {reordering && (
        <div className="alert alert-info">
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Saving...</span>
            </div>
            <span>Saving lesson order...</span>
          </div>
        </div>
      )}
      
      <div className="card mb-4">
        <div className="card-body">
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : lessons.length === 0 ? (
            <div className="text-center my-5">
              <p>No lessons found for this course. Add your first lesson to get started!</p>
              <Link to={`/apps/${appId}/courses/${courseId}/lessons/new`} className="btn btn-primary">
                <i className="fas fa-plus"></i> Add New Lesson
              </Link>
            </div>
          ) : (
            <div>
              <p className="text-muted mb-3">
                <i className="fas fa-info-circle me-2"></i>
                Drag and drop lessons to reorder them, or use the up/down arrows.
              </p>
              
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="lessons">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="lessons-list"
                    >
                      {lessons.map((lesson, index) => (
                        <Draggable key={lesson.id} draggableId={lesson.id.toString()} index={index}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className="lesson-item card mb-3"
                            >
                              <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div className="d-flex align-items-center">
                                    <div {...provided.dragHandleProps} className="drag-handle me-3">
                                      <i className="fas fa-grip-vertical"></i>
                                    </div>
                                    <div>
                                      <h5 className="mb-1">{lesson.title}</h5>
                                      <div className="d-flex align-items-center">
                                        <span className={`badge ${lesson.is_published ? 'bg-success' : 'bg-warning'} me-2`}>
                                          {lesson.is_published ? 'Published' : 'Draft'}
                                        </span>
                                        <small className="text-muted">
                                          Order: {lesson.order_index}
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="d-flex align-items-center">
                                    {/* Fallback reordering buttons */}
                                    <div className="btn-group me-3">
                                      <button 
                                        className="btn btn-sm btn-outline-secondary" 
                                        onClick={() => handleMoveUp(index)}
                                        disabled={index === 0}
                                        title="Move Up"
                                      >
                                        <i className="fas fa-arrow-up"></i>
                                      </button>
                                      <button 
                                        className="btn btn-sm btn-outline-secondary" 
                                        onClick={() => handleMoveDown(index)}
                                        disabled={index === lessons.length - 1}
                                        title="Move Down"
                                      >
                                        <i className="fas fa-arrow-down"></i>
                                      </button>
                                    </div>
                                    
                                    <div className="btn-group">
                                      <Link 
                                        to={`/apps/${appId}/courses/${courseId}/lessons/${lesson.id}`} 
                                        className="btn btn-sm btn-outline-primary"
                                        title="View Lesson"
                                      >
                                        <i className="fas fa-eye"></i>
                                      </Link>
                                      <Link 
                                        to={`/apps/${appId}/courses/${courseId}/lessons/${lesson.id}/edit`} 
                                        className="btn btn-sm btn-outline-secondary"
                                        title="Edit Lesson"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </Link>
                                      <button 
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDeleteLesson(lesson.id)}
                                        title="Delete Lesson"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                
                                {lesson.video_url && (
                                  <div className="mt-3">
                                    <span className="badge bg-info me-2">
                                      <i className="fas fa-video me-1"></i> Has Video
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonManagement; 